import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  FrontdeskConfirmCleaningRequestSchema,
  FrontdeskLightOffFactSchema,
  isFrontdeskTableCloseoutReceiptForRequest,
} from "./frontdesk-operating-fact.js";
import { KioskProductReservationStateSchema } from "./kiosk-product-order.js";

const ServiceDutyIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const ServiceDutyIdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const ServiceDutyDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const ServiceDutyChineseTextSchema = Type.String({
  minLength: 2,
  maxLength: 300,
  pattern: "[\\u3400-\\u9FFF]",
});
const ServiceDutyVersionSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const ServiceDutySlaMinutesSchema = Type.Integer({ minimum: 1, maximum: 1_440 });

export const ServiceRoutingRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
]);
export type ServiceRoutingRole = Static<typeof ServiceRoutingRoleSchema>;

const ServiceRoutingRoleFields = {
  primaryRole: ServiceRoutingRoleSchema,
  copiedRoles: Type.Array(ServiceRoutingRoleSchema, { maxItems: 2, uniqueItems: true }),
  allowCashierTemporaryDuty: Type.Boolean(),
};

export const ServiceRoutingConfigurationReadModelSchema = Type.Object(
  {
    configVersion: ServiceDutyVersionSchema,
    ...ServiceRoutingRoleFields,
    serviceSlaMinutes: Type.Union([ServiceDutySlaMinutesSchema, Type.Null()]),
    checkoutSlaMinutes: Type.Union([ServiceDutySlaMinutesSchema, Type.Null()]),
    ownerEscalationDelayMinutes: Type.Union([ServiceDutySlaMinutesSchema, Type.Null()]),
    updatedAt: ServiceDutyDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ServiceRoutingConfigurationReadModel = Static<
  typeof ServiceRoutingConfigurationReadModelSchema
>;

export const UpdateServiceRoutingConfigurationCommandSchema = Type.Object(
  {
    expectedConfigVersion: ServiceDutyVersionSchema,
    ...ServiceRoutingRoleFields,
    serviceSlaMinutes: ServiceDutySlaMinutesSchema,
    checkoutSlaMinutes: ServiceDutySlaMinutesSchema,
    ownerEscalationDelayMinutes: ServiceDutySlaMinutesSchema,
    idempotencyKey: ServiceDutyIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type UpdateServiceRoutingConfigurationCommand = Static<
  typeof UpdateServiceRoutingConfigurationCommandSchema
>;

export const ServiceDutyStatusSchema = Type.Union([
  Type.Literal("INACTIVE"),
  Type.Literal("ACTIVE"),
]);
export type ServiceDutyStatus = Static<typeof ServiceDutyStatusSchema>;

export const ServiceDutyAssigneeSummarySchema = Type.Object(
  {
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    role: Type.Literal("CASHIER"),
    assumedAt: ServiceDutyDateTimeSchema,
    isCurrentActor: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type ServiceDutyAssigneeSummary = Static<typeof ServiceDutyAssigneeSummarySchema>;

export const ServiceDutyReadModelSchema = Type.Object(
  {
    configuration: ServiceRoutingConfigurationReadModelSchema,
    dutyVersion: ServiceDutyVersionSchema,
    status: ServiceDutyStatusSchema,
    currentAssignee: Type.Union([ServiceDutyAssigneeSummarySchema, Type.Null()]),
    incompleteClaimedCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    canConfigure: Type.Boolean(),
    canAssume: Type.Boolean(),
    canRelease: Type.Boolean(),
    assumeUnavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
    releaseUnavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
    generatedAt: ServiceDutyDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ServiceDutyReadModel = Static<typeof ServiceDutyReadModelSchema>;

export const AssumeServiceDutyCommandSchema = Type.Object(
  {
    expectedConfigVersion: ServiceDutyVersionSchema,
    expectedDutyVersion: ServiceDutyVersionSchema,
    idempotencyKey: ServiceDutyIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AssumeServiceDutyCommand = Static<typeof AssumeServiceDutyCommandSchema>;

export const ReleaseServiceDutyCommandSchema = Type.Object(
  {
    expectedDutyVersion: ServiceDutyVersionSchema,
    idempotencyKey: ServiceDutyIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReleaseServiceDutyCommand = Static<typeof ReleaseServiceDutyCommandSchema>;

export const ServiceWorkItemSourceStateSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("ACKNOWLEDGED"),
  Type.Literal("COMPLETED"),
  Type.Literal("FAILED"),
]);
export type ServiceWorkItemSourceState = Static<typeof ServiceWorkItemSourceStateSchema>;

export const ServiceWorkItemSourceSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("KIOSK_PRODUCT_ORDER"),
      type: Type.Literal("PRODUCT_DELIVERY"),
      state: ServiceWorkItemSourceStateSchema,
      reservationId: ServiceDutyIdSchema,
      reservationVersion: ServiceDutyVersionSchema,
      reservationState: KioskProductReservationStateSchema,
      sessionId: ServiceDutyIdSchema,
      orderId: ServiceDutyIdSchema,
      skuId: ServiceDutyIdSchema,
      productVersion: ServiceDutyVersionSchema,
      productName: Type.String({ minLength: 1, maxLength: 120 }),
      unit: Type.String({ minLength: 1, maxLength: 32 }),
      unitPriceFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      quantity: Type.Integer({ minimum: 1, maximum: 99 }),
      lineTotalFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SERVICE_REQUEST"),
      eventId: ServiceDutyIdSchema,
      type: Type.Union([
        Type.Literal("SERVICE"),
        Type.Literal("COACH"),
        Type.Literal("CHECKOUT"),
        Type.Literal("OPEN_TABLE"),
      ]),
      state: ServiceWorkItemSourceStateSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("TABLE_CLEANING"),
      type: Type.Literal("TABLE_CLEANING"),
      state: ServiceWorkItemSourceStateSchema,
      tableId: ServiceDutyIdSchema,
      orderId: ServiceDutyIdSchema,
      sessionId: ServiceDutyIdSchema,
      tableRevision: ServiceDutyVersionSchema,
      aggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      lightOff: FrontdeskLightOffFactSchema,
    },
    { additionalProperties: false },
  ),
]);
export type ServiceWorkItemSource = Static<typeof ServiceWorkItemSourceSchema>;

export const ServiceWorkItemPrioritySchema = Type.Union([
  Type.Literal("URGENT"),
  Type.Literal("HIGH"),
  Type.Literal("NORMAL"),
  Type.Literal("LOW"),
]);
export type ServiceWorkItemPriority = Static<typeof ServiceWorkItemPrioritySchema>;

export const ServiceWorkItemSlaStatusSchema = Type.Union([
  Type.Literal("NOT_CONFIGURED"),
  Type.Literal("ON_TIME"),
  Type.Literal("DUE_SOON"),
  Type.Literal("OVERDUE"),
  Type.Literal("COMPLETED_ON_TIME"),
  Type.Literal("COMPLETED_LATE"),
  Type.Literal("FAILED"),
]);
export type ServiceWorkItemSlaStatus = Static<typeof ServiceWorkItemSlaStatusSchema>;

export const ServiceWorkItemEscalationStatusSchema = Type.Union([
  Type.Literal("NONE"),
  Type.Literal("MANAGER_NOTIFIED"),
  Type.Literal("OWNER_NOTIFIED"),
]);
export type ServiceWorkItemEscalationStatus = Static<typeof ServiceWorkItemEscalationStatusSchema>;

export const ServiceWorkItemCoverageStatusSchema = Type.Union([
  Type.Literal("COVERED"),
  Type.Literal("UNATTENDED"),
  Type.Literal("UNKNOWN"),
]);
export type ServiceWorkItemCoverageStatus = Static<typeof ServiceWorkItemCoverageStatusSchema>;

export const ServiceWorkItemViewerActionsSchema = Type.Object(
  {
    canAcknowledge: Type.Boolean(),
    canComplete: Type.Boolean(),
    unavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
    canTransfer: Type.Boolean(),
    transferUnavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type ServiceWorkItemViewerActions = Static<typeof ServiceWorkItemViewerActionsSchema>;

export const ServiceWorkItemReadModelSchema = Type.Object(
  {
    workItemId: ServiceDutyIdSchema,
    workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    source: ServiceWorkItemSourceSchema,
    primaryRole: ServiceRoutingRoleSchema,
    copiedRoles: Type.Array(ServiceRoutingRoleSchema, { maxItems: 2, uniqueItems: true }),
    priority: ServiceWorkItemPrioritySchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    title: ServiceDutyChineseTextSchema,
    summary: ServiceDutyChineseTextSchema,
    statusLabel: ServiceDutyChineseTextSchema,
    statusExplanation: ServiceDutyChineseTextSchema,
    nextStepLabel: ServiceDutyChineseTextSchema,
    requestedAt: ServiceDutyDateTimeSchema,
    dueAt: Type.Union([ServiceDutyDateTimeSchema, Type.Null()]),
    updatedAt: ServiceDutyDateTimeSchema,
    completedAt: Type.Union([ServiceDutyDateTimeSchema, Type.Null()]),
    slaStatus: ServiceWorkItemSlaStatusSchema,
    escalationStatus: ServiceWorkItemEscalationStatusSchema,
    escalatedAt: Type.Union([ServiceDutyDateTimeSchema, Type.Null()]),
    coverageStatus: ServiceWorkItemCoverageStatusSchema,
    coverageReason: ServiceDutyChineseTextSchema,
    escalationReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
    assigneeDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    assignedToCurrentActor: Type.Boolean(),
    viewerActions: ServiceWorkItemViewerActionsSchema,
  },
  { additionalProperties: false },
);
export type ServiceWorkItemReadModel = Static<typeof ServiceWorkItemReadModelSchema>;

export const ServiceWorkItemListReadModelSchema = Type.Object(
  {
    items: Type.Array(ServiceWorkItemReadModelSchema, { maxItems: 500 }),
    activeCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    overdueCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: ServiceDutyDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ServiceWorkItemListReadModel = Static<typeof ServiceWorkItemListReadModelSchema>;

/**
 * Completes only the cleaning work item and the existing table-cleaning fact.
 * It never grants table release, funds or inventory mutation authority.
 */
export const CompleteTableCleaningWorkItemCommandSchema = Type.Object(
  {
    workItemId: ServiceDutyIdSchema,
    expectedWorkItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    tableId: ServiceDutyIdSchema,
    orderId: ServiceDutyIdSchema,
    request: FrontdeskConfirmCleaningRequestSchema,
  },
  { additionalProperties: false },
);
export type CompleteTableCleaningWorkItemCommand = Static<
  typeof CompleteTableCleaningWorkItemCommandSchema
>;

export const TableCleaningWorkItemCompletionReceiptSchema = Type.Object(
  {
    workItemId: ServiceDutyIdSchema,
    workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: ServiceDutyIdempotencyKeySchema,
    closeout: Type.Object(
      {
        eventType: Type.Literal("TABLE_CLEANING_CONFIRMED"),
        tableId: ServiceDutyIdSchema,
        sessionId: ServiceDutyIdSchema,
        orderId: ServiceDutyIdSchema,
        aggregateVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
        tableRevision: ServiceDutyVersionSchema,
        tableState: Type.Literal("AWAITING_CLEANING"),
        cleaningState: Type.Literal("CLEANING_COMPLETED"),
        occurredAt: ServiceDutyDateTimeSchema,
      },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export type TableCleaningWorkItemCompletionReceipt = Static<
  typeof TableCleaningWorkItemCompletionReceiptSchema
>;

export const ServiceRequestTransferAssigneeSummarySchema = Type.Object(
  {
    candidateId: ServiceDutyIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    role: ServiceRoutingRoleSchema,
  },
  { additionalProperties: false },
);
export type ServiceRequestTransferAssigneeSummary = Static<
  typeof ServiceRequestTransferAssigneeSummarySchema
>;

export const ServiceRequestTransferRoleOptionSchema = Type.Object(
  {
    role: ServiceRoutingRoleSchema,
    label: ServiceDutyChineseTextSchema,
    availableCandidateCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    canSelect: Type.Boolean(),
    unavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type ServiceRequestTransferRoleOption = Static<
  typeof ServiceRequestTransferRoleOptionSchema
>;

export const ServiceRequestTransferCandidateOptionSchema = Type.Object(
  {
    candidateId: ServiceDutyIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    role: ServiceRoutingRoleSchema,
    canSelect: Type.Boolean(),
    unavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type ServiceRequestTransferCandidateOption = Static<
  typeof ServiceRequestTransferCandidateOptionSchema
>;

export const ServiceRequestTransferOptionsReadModelSchema = Type.Object(
  {
    eventId: ServiceDutyIdSchema,
    workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    currentAssignee: Type.Union([ServiceRequestTransferAssigneeSummarySchema, Type.Null()]),
    roles: Type.Array(ServiceRequestTransferRoleOptionSchema, {
      minItems: 1,
      maxItems: 3,
    }),
    candidates: Type.Array(ServiceRequestTransferCandidateOptionSchema, { maxItems: 500 }),
    canTransfer: Type.Boolean(),
    transferUnavailableReason: Type.Union([ServiceDutyChineseTextSchema, Type.Null()]),
    generatedAt: ServiceDutyDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ServiceRequestTransferOptionsReadModel = Static<
  typeof ServiceRequestTransferOptionsReadModelSchema
>;

export const TransferServiceRequestCommandSchema = Type.Object(
  {
    expectedWorkItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    targetRole: ServiceRoutingRoleSchema,
    targetCandidateId: Type.Union([ServiceDutyIdSchema, Type.Null()]),
    reason: ServiceDutyChineseTextSchema,
    idempotencyKey: ServiceDutyIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type TransferServiceRequestCommand = Static<typeof TransferServiceRequestCommandSchema>;

export const ServiceRequestTransferReceiptSchema = Type.Object(
  {
    eventId: ServiceDutyIdSchema,
    workItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    targetRole: ServiceRoutingRoleSchema,
    targetCandidateId: Type.Union([ServiceDutyIdSchema, Type.Null()]),
    reason: ServiceDutyChineseTextSchema,
    transferredAt: ServiceDutyDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ServiceRequestTransferReceipt = Static<typeof ServiceRequestTransferReceiptSchema>;

function isValidRoleRouting(
  value: Pick<ServiceRoutingConfigurationReadModel, "primaryRole" | "copiedRoles">,
): boolean {
  return (
    new Set(value.copiedRoles).size === value.copiedRoles.length &&
    !value.copiedRoles.includes(value.primaryRole)
  );
}

function isValidRoutingConfiguration(
  value: Pick<
    ServiceRoutingConfigurationReadModel,
    "primaryRole" | "copiedRoles" | "allowCashierTemporaryDuty"
  >,
): boolean {
  return (
    isValidRoleRouting(value) &&
    (value.primaryRole !== "CASHIER" || value.allowCashierTemporaryDuty)
  );
}

function timestamp(value: string): number | null {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value
    ? milliseconds
    : null;
}

function actionAvailabilityIsConsistent(value: ServiceWorkItemReadModel): boolean {
  const { canAcknowledge, canComplete, unavailableReason, canTransfer, transferUnavailableReason } =
    value.viewerActions;
  const pendingCleaningCompletion =
    value.source.kind === "TABLE_CLEANING" && value.source.state === "PENDING";
  if (canAcknowledge && canComplete) return false;
  if ((canAcknowledge || canComplete) !== (unavailableReason === null)) return false;
  if (canTransfer !== (transferUnavailableReason === null)) return false;
  if (canAcknowledge && value.source.state !== "PENDING") return false;
  if (canComplete && value.source.state !== "ACKNOWLEDGED" && !pendingCleaningCompletion) {
    return false;
  }
  if (value.source.kind === "TABLE_CLEANING") {
    if (canAcknowledge || canTransfer) return false;
  }
  if (canTransfer && value.source.state !== "PENDING" && value.source.state !== "ACKNOWLEDGED") {
    return false;
  }
  if (
    (value.source.state === "COMPLETED" || value.source.state === "FAILED") &&
    (canAcknowledge || canComplete || canTransfer)
  ) {
    return false;
  }
  if (value.coverageStatus === "UNKNOWN" && (canAcknowledge || canComplete || canTransfer)) {
    return false;
  }
  if (value.coverageStatus === "UNATTENDED" && (canAcknowledge || canComplete)) return false;
  return true;
}

function coverageIsConsistent(value: ServiceWorkItemReadModel): boolean {
  const assigned = value.assigneeDisplayName !== null;
  if (assigned && value.coverageStatus !== "COVERED") return false;
  return (value.escalationStatus === "NONE") === (value.escalationReason === null);
}

function isImmediateUnattendedManagerEscalation(value: ServiceWorkItemReadModel): boolean {
  return (
    (value.source.state === "PENDING" || value.source.state === "ACKNOWLEDGED") &&
    value.coverageStatus === "UNATTENDED" &&
    value.escalationStatus === "MANAGER_NOTIFIED" &&
    value.escalatedAt !== null
  );
}

function sourceStateIsConsistent(value: ServiceWorkItemReadModel): boolean {
  const assigned = value.assigneeDisplayName !== null;
  if (value.assignedToCurrentActor && !assigned) return false;
  switch (value.source.state) {
    case "PENDING":
      return !assigned && !value.assignedToCurrentActor && value.completedAt === null;
    case "ACKNOWLEDGED":
      return assigned && value.completedAt === null;
    case "COMPLETED":
      return assigned && value.completedAt !== null;
    case "FAILED":
      return value.completedAt === null;
  }
}

function slaStatusIsConsistent(value: ServiceWorkItemReadModel): boolean {
  if (value.source.state === "PENDING" || value.source.state === "ACKNOWLEDGED") {
    if (value.dueAt === null) {
      return (
        value.slaStatus === "NOT_CONFIGURED" &&
        (value.escalationStatus === "NONE" || isImmediateUnattendedManagerEscalation(value))
      );
    }
    return (
      value.slaStatus === "ON_TIME" ||
      value.slaStatus === "DUE_SOON" ||
      value.slaStatus === "OVERDUE"
    );
  }
  if (value.source.state === "FAILED") return value.slaStatus === "FAILED";
  if (value.completedAt === null) return false;
  if (value.dueAt === null) return value.slaStatus === "NOT_CONFIGURED";
  const completedAt = timestamp(value.completedAt);
  const dueAt = timestamp(value.dueAt);
  if (completedAt === null || dueAt === null) return false;
  return completedAt <= dueAt
    ? value.slaStatus === "COMPLETED_ON_TIME"
    : value.slaStatus === "COMPLETED_LATE";
}

export function isServiceRoutingConfigurationReadModel(
  value: unknown,
): value is ServiceRoutingConfigurationReadModel {
  if (!Value.Check(ServiceRoutingConfigurationReadModelSchema, value)) return false;
  if (!isValidRoutingConfiguration(value)) return false;
  const slaValues = [
    value.serviceSlaMinutes,
    value.checkoutSlaMinutes,
    value.ownerEscalationDelayMinutes,
  ];
  const allMissing = slaValues.every((item) => item === null);
  const allConfigured = slaValues.every((item) => item !== null);
  return value.configVersion === 0 ? allMissing : allConfigured;
}

export function isUpdateServiceRoutingConfigurationCommand(
  value: unknown,
): value is UpdateServiceRoutingConfigurationCommand {
  return (
    Value.Check(UpdateServiceRoutingConfigurationCommandSchema, value) &&
    isValidRoutingConfiguration(value)
  );
}

export function isServiceDutyReadModel(value: unknown): value is ServiceDutyReadModel {
  if (!Value.Check(ServiceDutyReadModelSchema, value)) return false;
  if (!isServiceRoutingConfigurationReadModel(value.configuration)) return false;
  const generatedAt = timestamp(value.generatedAt);
  const configUpdatedAt = timestamp(value.configuration.updatedAt);
  if (generatedAt === null || configUpdatedAt === null || generatedAt < configUpdatedAt)
    return false;
  if (value.canAssume && value.canRelease) return false;
  if (value.canAssume !== (value.assumeUnavailableReason === null)) return false;
  if (value.canRelease !== (value.releaseUnavailableReason === null)) return false;
  if (value.status === "INACTIVE") {
    return (
      value.currentAssignee === null &&
      value.incompleteClaimedCount === 0 &&
      !value.canRelease &&
      (!value.canAssume || value.configuration.allowCashierTemporaryDuty)
    );
  }
  if (value.currentAssignee === null || value.canAssume) return false;
  const assumedAt = timestamp(value.currentAssignee.assumedAt);
  if (assumedAt === null || assumedAt > generatedAt) return false;
  if (value.incompleteClaimedCount > 0 && value.canRelease) return false;
  if (value.canRelease && !value.currentAssignee.isCurrentActor) return false;
  return true;
}

export function isAssumeServiceDutyCommand(value: unknown): value is AssumeServiceDutyCommand {
  return Value.Check(AssumeServiceDutyCommandSchema, value);
}

export function isReleaseServiceDutyCommand(value: unknown): value is ReleaseServiceDutyCommand {
  return Value.Check(ReleaseServiceDutyCommandSchema, value);
}

export function isCompleteTableCleaningWorkItemCommand(
  value: unknown,
): value is CompleteTableCleaningWorkItemCommand {
  return (
    Value.Check(CompleteTableCleaningWorkItemCommandSchema, value) &&
    value.expectedWorkItemVersion === value.request.expectedAggregateVersion
  );
}

export function isTableCleaningWorkItemCompletionReceipt(
  value: unknown,
): value is TableCleaningWorkItemCompletionReceipt {
  return Value.Check(TableCleaningWorkItemCompletionReceiptSchema, value);
}

export function isTableCleaningWorkItemCompletionReceiptForCommand(
  value: unknown,
  command: CompleteTableCleaningWorkItemCommand,
): value is TableCleaningWorkItemCompletionReceipt {
  return (
    isTableCleaningWorkItemCompletionReceipt(value) &&
    value.workItemId === command.workItemId &&
    value.workItemVersion === command.expectedWorkItemVersion + 1 &&
    value.idempotencyKey === command.request.idempotencyKey &&
    value.closeout.orderId === command.orderId &&
    isFrontdeskTableCloseoutReceiptForRequest(
      value.closeout,
      command.request,
      "TABLE_CLEANING_CONFIRMED",
      command.tableId,
    )
  );
}

export function isTransferServiceRequestCommand(
  value: unknown,
): value is TransferServiceRequestCommand {
  return Value.Check(TransferServiceRequestCommandSchema, value);
}

export function isServiceRequestTransferOptionsReadModel(
  value: unknown,
): value is ServiceRequestTransferOptionsReadModel {
  if (!Value.Check(ServiceRequestTransferOptionsReadModelSchema, value)) return false;
  if (timestamp(value.generatedAt) === null) return false;
  if (value.canTransfer !== (value.transferUnavailableReason === null)) return false;
  if (new Set(value.roles.map((option) => option.role)).size !== value.roles.length) return false;
  if (
    new Set(value.candidates.map((option) => option.candidateId)).size !== value.candidates.length
  )
    return false;
  const roles = new Set(value.roles.map((option) => option.role));
  if (value.candidates.some((candidate) => !roles.has(candidate.role))) return false;
  if (
    value.roles.some((option) => {
      const candidateCount = value.candidates.filter(
        (candidate) => candidate.role === option.role && candidate.canSelect,
      ).length;
      return (
        candidateCount !== option.availableCandidateCount ||
        option.canSelect !== (option.unavailableReason === null) ||
        option.canSelect !== candidateCount > 0
      );
    })
  ) {
    return false;
  }
  if (
    value.candidates.some(
      (candidate) => candidate.canSelect !== (candidate.unavailableReason === null),
    )
  ) {
    return false;
  }
  if (value.currentAssignee) {
    const currentCandidate = value.candidates.find(
      (candidate) => candidate.candidateId === value.currentAssignee?.candidateId,
    );
    if (
      !currentCandidate ||
      currentCandidate.displayName !== value.currentAssignee.displayName ||
      currentCandidate.role !== value.currentAssignee.role ||
      currentCandidate.canSelect
    ) {
      return false;
    }
  }
  const hasSelectableTarget =
    value.roles.some((option) => option.canSelect) ||
    value.candidates.some((option) => option.canSelect);
  return !value.canTransfer || hasSelectableTarget;
}

export function isServiceRequestTransferReceipt(
  value: unknown,
): value is ServiceRequestTransferReceipt {
  return (
    Value.Check(ServiceRequestTransferReceiptSchema, value) &&
    timestamp(value.transferredAt) !== null
  );
}

export function isServiceWorkItemReadModel(value: unknown): value is ServiceWorkItemReadModel {
  if (!Value.Check(ServiceWorkItemReadModelSchema, value)) return false;
  if (!isValidRoleRouting(value)) return false;
  if (
    value.source.kind === "SERVICE_REQUEST" &&
    value.workItemId !== `service:${value.source.eventId}`
  ) {
    return false;
  }
  if (
    value.source.kind === "TABLE_CLEANING" &&
    (value.primaryRole !== "WAITER" ||
      value.workItemId !== `table-cleaning:${value.source.sessionId}` ||
      value.workItemVersion !== value.source.aggregateVersion ||
      value.source.state === "ACKNOWLEDGED")
  ) {
    return false;
  }
  if (value.source.kind === "KIOSK_PRODUCT_ORDER") {
    const source = value.source;
    const expectedLineTotalFen = source.unitPriceFen * source.quantity;
    const expectedSourceState =
      source.reservationState === "RESERVED"
        ? "PENDING"
        : source.reservationState === "ACKNOWLEDGED"
          ? "ACKNOWLEDGED"
          : source.reservationState === "DELIVERED"
            ? "COMPLETED"
            : "FAILED";
    if (
      source.state !== expectedSourceState ||
      !Number.isSafeInteger(expectedLineTotalFen) ||
      source.lineTotalFen !== expectedLineTotalFen ||
      value.workItemId !== `kiosk-product-order:${source.reservationId}` ||
      value.workItemVersion !== source.reservationVersion
    ) {
      return false;
    }
  }
  const requestedAt = timestamp(value.requestedAt);
  const dueAt = value.dueAt === null ? null : timestamp(value.dueAt);
  const updatedAt = timestamp(value.updatedAt);
  const completedAt = value.completedAt === null ? null : timestamp(value.completedAt);
  const escalatedAt = value.escalatedAt === null ? null : timestamp(value.escalatedAt);
  if (
    requestedAt === null ||
    updatedAt === null ||
    (value.dueAt !== null && dueAt === null) ||
    (dueAt !== null && dueAt < requestedAt) ||
    updatedAt < requestedAt ||
    (value.completedAt !== null && completedAt === null) ||
    (completedAt !== null && (completedAt < requestedAt || updatedAt < completedAt)) ||
    (value.escalatedAt !== null && escalatedAt === null) ||
    (escalatedAt !== null && (escalatedAt < requestedAt || updatedAt < escalatedAt))
  ) {
    return false;
  }
  if (value.source.kind === "TABLE_CLEANING") {
    const { status, evidence, updatedAt: lightOffUpdatedAtValue } = value.source.lightOff;
    const lightOffUpdatedAt =
      lightOffUpdatedAtValue === null ? null : timestamp(lightOffUpdatedAtValue);
    if (
      (status === "NOT_REQUIRED" && (evidence !== null || lightOffUpdatedAtValue !== null)) ||
      (status === "UNKNOWN" && lightOffUpdatedAtValue === null && evidence !== null) ||
      (status !== "NOT_REQUIRED" &&
        !(status === "UNKNOWN" && lightOffUpdatedAtValue === null) &&
        lightOffUpdatedAt === null) ||
      ((status === "CONFIRMED" || status === "FAILED") && evidence === null) ||
      (lightOffUpdatedAt !== null && lightOffUpdatedAt > updatedAt)
    ) {
      return false;
    }
  }
  if (
    value.dueAt === null &&
    value.slaStatus !== "NOT_CONFIGURED" &&
    !(value.source.state === "FAILED" && value.slaStatus === "FAILED")
  ) {
    return false;
  }
  if (value.dueAt !== null && value.slaStatus === "NOT_CONFIGURED") return false;
  if ((value.escalationStatus === "NONE") !== (value.escalatedAt === null)) return false;
  if (
    value.escalationStatus !== "NONE" &&
    !isImmediateUnattendedManagerEscalation(value) &&
    (dueAt === null || escalatedAt === null || escalatedAt < dueAt || value.slaStatus !== "OVERDUE")
  ) {
    return false;
  }
  return (
    sourceStateIsConsistent(value) &&
    coverageIsConsistent(value) &&
    actionAvailabilityIsConsistent(value) &&
    slaStatusIsConsistent(value)
  );
}

const serviceWorkItemPriorityRank: Record<ServiceWorkItemPriority, number> = {
  URGENT: 0,
  HIGH: 1,
  NORMAL: 2,
  LOW: 3,
};

export function compareServiceWorkItems(
  left: ServiceWorkItemReadModel,
  right: ServiceWorkItemReadModel,
): number {
  const priorityOrder =
    serviceWorkItemPriorityRank[left.priority] - serviceWorkItemPriorityRank[right.priority];
  if (priorityOrder !== 0) return priorityOrder;
  const leftDueAt = left.dueAt === null ? Number.POSITIVE_INFINITY : Date.parse(left.dueAt);
  const rightDueAt = right.dueAt === null ? Number.POSITIVE_INFINITY : Date.parse(right.dueAt);
  if (leftDueAt !== rightDueAt) return leftDueAt - rightDueAt;
  const requestedOrder = Date.parse(left.requestedAt) - Date.parse(right.requestedAt);
  return requestedOrder === 0 ? left.workItemId.localeCompare(right.workItemId) : requestedOrder;
}

export function isServiceWorkItemListReadModel(
  value: unknown,
): value is ServiceWorkItemListReadModel {
  if (!Value.Check(ServiceWorkItemListReadModelSchema, value)) return false;
  if (!value.items.every(isServiceWorkItemReadModel)) return false;
  if (new Set(value.items.map((item) => item.workItemId)).size !== value.items.length) return false;
  const activeCount = value.items.filter(
    (item) => item.source.state === "PENDING" || item.source.state === "ACKNOWLEDGED",
  ).length;
  const overdueCount = value.items.filter(
    (item) =>
      (item.source.state === "PENDING" || item.source.state === "ACKNOWLEDGED") &&
      item.slaStatus === "OVERDUE",
  ).length;
  if (value.activeCount !== activeCount || value.overdueCount !== overdueCount) return false;
  return value.items.every((item, index) => {
    const previous = value.items[index - 1];
    if (!previous) return true;
    return compareServiceWorkItems(previous, item) <= 0;
  });
}
